import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { FormattedMessage } from '@flipster/i18n';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { IconButton } from '@flipster/react/design-system/icon-button';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import TrashIcon from '@flipster/react/icon/generated/TrashIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { useDeleteTpSl } from '../../trade-actions';
import { tradingSessionCheckBunja } from '../../trading-suspension/state';
import type { TpslButtonStateProps } from './identity';
import { tpslButtonBunja } from './state';
import { useTpslManageClick } from './use-tpsl-manage-click';

/**
 * TP/SL 복수(count>1) — 개수 칩(관리 모달 진입) + 전체삭제 아이콘.
 * 개수·slot·symbol을 tpslButtonBunja에서 직접 구독한다(position 객체를 받지 않는다).
 */
export const TpslButtonMulti = (props: TpslButtonStateProps) => {
  const { tpslType, accountId, disabled } = props;
  const t = useTranslations();
  const isCopyTrading = typeof accountId === 'number';

  const { symbolAtom, tpCountAtom, slCountAtom, slotAtom } = useBunja(tpslButtonBunja);
  const symbol = useAtomValue(symbolAtom);
  const count = useAtomValue(tpslType === 'tp' ? tpCountAtom : slCountAtom);
  const slot = useAtomValue(slotAtom);

  const { checkTradable } = useBunja(tradingSessionCheckBunja);
  const { deleteAllTpslQuery } = useDeleteTpSl();
  const { isPending: isLoadingDeleteAll, mutateAsync: deleteAllTpsl } = deleteAllTpslQuery;

  // 복수 상태는 거래 불가여도 관리 모달을 열 수 있어야 하므로 클릭 가드를 두지 않는다.
  const handleOpen = useTpslManageClick({ ...props, guardTradable: false });

  const handleRemoveAll = () => {
    if (!checkTradable()) return;
    dialog.open({
      title: { label: t(tpslType === 'tp' ? 'Trade.r260330.Remove_all_tp' : 'Trade.r260330.Remove_all_sl') },
      content: t(
        tpslType === 'tp' ? 'Trade.r260330.Remove_all_tp_confirmation' : 'Trade.r260330.Remove_all_sl_confirmation',
      ),
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            size='large'
            label={<FormattedMessage id='Common.r250401.Confirm' />}
            isLoading={isLoadingDeleteAll}
            onClick={async () => {
              await deleteAllTpsl({ symbol, slot, tpslType });
              dialog.close();
            }}
          />
        ),
        secondary: (
          <Dialog.Button
            hierarchy='secondary'
            color='neutral'
            size='large'
            label={<FormattedMessage id='Common.r250401.Cancel' />}
            onClick={() => {
              dialog.close();
            }}
          />
        ),
      },
    });
  };

  return (
    <div className={hstack({ gap: 12 })}>
      <button
        disabled={disabled}
        className={css(
          hstack.raw({
            bg: 'background.accent.secondary.tint',
            color: 'content.default.level-5',
            gap: 2,
            py: 2,
            pl: 8,
            pr: 4,
            borderRadius: 1000,
            alignItems: 'center',
          }),
          disabled
            ? {
                bg: 'background.interaction.disabled',
                color: 'content.interaction.disabled',
                cursor: 'not-allowed',
              }
            : { _hover: { bg: 'background.accent.secondary.tint.hover' } },
        )}
        onClick={handleOpen}
      >
        <div className={css({ textStyle: 'Text/XS/monospace/regular' })}>
          <Formatted.Number value={count} />
        </div>
        <ChevronRightIcon
          size={12}
          color={token(!disabled ? 'colors.content.default.level-2' : 'colors.content.interaction.disabled')}
        />
      </button>
      {!isCopyTrading && (
        <IconButton
          hierarchy='quaternary'
          icon={<TrashIcon />}
          size='small'
          disabled={disabled}
          onClick={handleRemoveAll}
        />
      )}
    </div>
  );
};
